import { PropsWithChildren, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import { Notice } from '@calm-web/design-system';

import Loader from '@/components/Loader';
import CreditCardForm from '@/components/purchase/CreditCardForm';
import LoggedInAs from '@/components/purchase/LoggedInAs';
import PaymentError from '@/components/purchase/PaymentError';
import PaymentTypeSelector from '@/components/purchase/PaymentTypeSelector';
import PurchaseTerms from '@/components/purchase/PurchaseTerms';
import { getPurchaseError } from '@/components/purchase/utils/getPurchaseError';
import { SubscribePurchaseError, subscribePurchaseErrors } from '@/components/purchase/utils/Purchase';
import { useAnalytics } from '@/hooks/analytics';
import { LegacyCalmError } from '@/hooks/api/types';
import { usePurchaseSuccess } from '@/hooks/purchase/usePurchaseSuccess';
import { useAppLoadedState, usePurchaseParamsState, useUserState } from '@/hooks/store';
import { setUser } from '@/store/user/actions';

import messages from './messages';
import { Link, NoticeWrapper, Wrapper } from './styles';
import {
	BEFORE_PURCHASE_STATE,
	OnGenericPaymentSubmit,
	OnPurchaseCancel,
	OnPurchaseError,
	OnPurchaseSuccess,
	PaymentType,
	PURCHASED_STATE,
	PurchaseFormProps,
	PurchaseState,
	PURCHASING_STATE,
	RequestInfo,
} from './types';

const PurchaseForm = ({
	buttonBgColor = 'buttonBlue',
	buttonTextColor,
	onSuccess: _onSuccess,
	ctaCopy,
	hideTerms,
	showLoggedInAs,
	purchaseTerms,
	isDisabled = false,
	onLogout,
	onPurchase,
	hidePriceAfterDisclaimer = true,
	centerFormElements = false,
	analyticsPrefix = 'Subscribe : Purchase : Form',
	children = null,
	initialNameValue = '',
	ignoreSubscribeErrors = false,
	updateUserOnSuccess = true,
	hideAlternativePaymentMethods = false,
	hidePaypal = false,
	hideApplePay = false,
	alignment = 'left',
	ignoreSuccessLogging = false,
	isSecondFreeTrial = false,
	hideBillingAddressFields = false,
	isMonthlyPricingTest = false,
	shouldUsePaymentFormPrice,
	...props
}: PropsWithChildren<PurchaseFormProps>) => {
	const _email = props.email ?? null;
	const appLoaded = useAppLoadedState();
	const purchaseParams = usePurchaseParamsState();
	const user = useUserState();

	const dispatch = useDispatch();
	const { formatMessage } = useIntl();
	const { logEvent } = useAnalytics();
	const logPurchaseSuccess = usePurchaseSuccess();
	const [showSamsungRedirect, setShowSamsungRedirect] = useState(false);
	const [showPurchaseSuccess, setShowPurchaseSuccess] = useState(false);

	const [purchaseState, setPurchaseState] = useState<PurchaseState>(BEFORE_PURCHASE_STATE);
	const [firstName, setFirstName] = useState(initialNameValue);
	const [currentPaymentType, setCurrentPaymentType] = useState<PaymentType | null>(
		hideAlternativePaymentMethods ? 'credit_card' : null,
	);
	const [errorCopy, setErrorCopy] = useState<string | null>('');
	const [isLoading, setIsLoading] = useState(false);
	const [noSubscribeError] = useState(
		ignoreSubscribeErrors
			? false
			: subscribePurchaseErrors
					.filter(value => (isSecondFreeTrial ? value !== 'previously-trialed' : true))
					.includes(purchaseParams?.purchaseType?.type as SubscribePurchaseError),
	);

	useEffect(() => {
		// TODO: refactor this code to follow best practices
		// eslint-disable-next-line react-hooks/set-state-in-effect
		setIsLoading(purchaseState === PURCHASING_STATE || purchaseState === PURCHASED_STATE);
	}, [purchaseState]);

	const onPurchaseError: OnPurchaseError = err => {
		setPurchaseState(BEFORE_PURCHASE_STATE);
		if (typeof err === 'string') {
			setErrorCopy(err);
		}
		const displayError = err as LegacyCalmError;
		setErrorCopy(formatMessage(getPurchaseError(displayError)));
		if (displayError.code === 'promotion_already_redeemed') {
			// Samsung-specific promotion error
			// show a link to the regular subscribe flow if this is the case
			setShowSamsungRedirect(true);
		}
	};

	const onPurchaseCancel: OnPurchaseCancel = (err, paymentType) => {
		setPurchaseState(BEFORE_PURCHASE_STATE);
		logEvent({
			eventName: `${analyticsPrefix} : Cancelled`,
			eventProps: {
				error: err,
				payment_type: paymentType,
			},
		});
		setCurrentPaymentType(null);
	};

	const onPurchaseSubmit: OnGenericPaymentSubmit = args => {
		setPurchaseState(PURCHASING_STATE);
		return onPurchase(args);
	};

	const onPurchaseSuccess: OnPurchaseSuccess = async (subscription, selectedPaymentType) => {
		setShowPurchaseSuccess(true);
		setPurchaseState(PURCHASED_STATE);
		const newUser = {
			...user,
			...subscription,
			id: user?.id,
			subscription,
		};
		if (updateUserOnSuccess) {
			dispatch(setUser(newUser));
		}

		if (!ignoreSuccessLogging) {
			await logPurchaseSuccess({
				analyticsPrefix,
				newUser,
				paymentType: selectedPaymentType,
			});
		}

		_onSuccess();
	};

	const createRequestInfo = (): RequestInfo => {
		const email = _email || user?.email;
		return {
			name: firstName,
			...(email ? { email } : {}),
		};
	};

	if (!appLoaded) return <Loader />;

	if (noSubscribeError) {
		return <PaymentError errorType={purchaseParams?.purchaseType?.type as SubscribePurchaseError} />;
	}

	let showCCForm = currentPaymentType === 'credit_card';

	if (showSamsungRedirect) {
		showCCForm = false;
	}

	return (
		<Wrapper $alignment={alignment} id="purchase-form">
			{!hideAlternativePaymentMethods && currentPaymentType !== 'credit_card' && (
				<PaymentTypeSelector
					currentPaymentType={currentPaymentType}
					setCurrentPaymentType={setCurrentPaymentType}
					onPurchaseSuccess={onPurchaseSuccess}
					onPurchaseError={onPurchaseError}
					onPurchaseSubmit={onPurchaseSubmit}
					onPurchaseCancel={onPurchaseCancel}
					requestInfo={createRequestInfo()}
					isLoading={isLoading}
					isDisabled={isDisabled}
					hidePaypal={hidePaypal}
					hideApplePay={hideApplePay}
					setErrorCopy={setErrorCopy}
					isMonthlyPricingTest={isMonthlyPricingTest}
					shouldUsePaymentFormPrice={shouldUsePaymentFormPrice}
				/>
			)}
			{showCCForm ? (
				<CreditCardForm
					buttonBgColor={buttonBgColor}
					buttonTextColor={buttonTextColor}
					ctaCopy={ctaCopy}
					hideTerms={hideTerms}
					hidePriceAfterDisclaimer={hidePriceAfterDisclaimer}
					centerFormElements={centerFormElements}
					analyticsPrefix={analyticsPrefix}
					purchaseState={purchaseState}
					firstName={firstName}
					setFirstName={setFirstName}
					setErrorCopy={setErrorCopy}
					requestInfo={createRequestInfo()}
					onPurchaseSubmit={onPurchaseSubmit}
					onPurchaseError={onPurchaseError}
					onPurchaseSuccess={onPurchaseSuccess}
					purchaseTerms={purchaseTerms || ''}
					isDisabled={isDisabled}
					setCurrentPaymentType={setCurrentPaymentType}
					hideBillingAddressFields={hideBillingAddressFields}
				>
					{children}
				</CreditCardForm>
			) : (
				!hideTerms &&
				!showSamsungRedirect && (
					<PurchaseTerms hidePriceAfterDisclaimer={hidePriceAfterDisclaimer} purchaseTerms={purchaseTerms} />
				)
			)}
			{children}
			{showLoggedInAs && <LoggedInAs onLogout={onLogout} email={_email} />}
			{showPurchaseSuccess && (
				<NoticeWrapper>
					<Notice isSuccess onClick={() => setShowPurchaseSuccess(false)} describedBy="purchase-form">
						{formatMessage(messages.success)}
					</Notice>
				</NoticeWrapper>
			)}
			{errorCopy && (
				<NoticeWrapper>
					<Notice isSuccess={false} onClick={() => setErrorCopy(null)} describedBy="purchase-form">
						{errorCopy}
					</Notice>
				</NoticeWrapper>
			)}
			{showSamsungRedirect && (
				<Link href={`/samsung/subscribe?calmjwt=${purchaseParams.calmjwt}`}>
					{formatMessage(messages.samsungSubscribeLink)}
				</Link>
			)}
		</Wrapper>
	);
};

export default PurchaseForm;
